import { CompoundPriceSpecification, Country, Service, UnitPriceSpecification, WithContext } from 'schema-dts'

import roamingCharges, { RoamingZones } from '@/roamingCharges'

import { roamingCharges as content } from '@/content.json'

const getCommonCode = (key: string) => {
  switch (key) {
    case 'data':
      return 'E63'
    case 'make_calls':
    case 'calls':
    case 'receive_calls':
      return '5K'
    case 'texts':
    case 'receive_texts':
    case 'send_texts':
    case 'mms_uk':
    case 'mms_row':
    default:
      return 'EA'
  }
}

const getCommonDesc = (key: string) => {
  switch (key) {
    case 'data':
      return 'per MB'
    case 'make_calls':
    case 'calls':
    case 'receive_calls':
      return 'per Minute'
    case 'mms_uk':
    case 'mms_row':
      return 'per MMS'
    case 'texts':
    case 'receive_texts':
    case 'send_texts':
    default:
      return 'per text'
  }
}

const formatPrice = (price: number | undefined) => {
  if (!price) return ''

  if (price === -1) return 'Free'

  return price / 100
}

type RoamingSchema = (WithContext<Service> | WithContext<CompoundPriceSpecification>)[]

export const buildRoamingSchema = (): RoamingSchema => {
  const paygZoneList = RoamingZones.map((zone): WithContext<Service> => {
    return {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: `Pay as you go roaming in ${zone.name}`,
      offers: {
        '@type': 'Offer',
        name: `Pay as you go roaming in ${zone.name}`,
        areaServed: roamingCharges.reduce<WithContext<Country>[]>((carry, charge) => {
          if (charge.zone === zone.name) {
            carry.push({
              '@context': 'https://schema.org',
              '@type': 'Country',
              name: charge.name,
            })
          }

          return carry
        }, []),
        availability: 'https://schema.org/InStock',
        priceSpecification: {
          '@type': 'CompoundPriceSpecification',
          name: `${zone.name} Pay as you go roaming tariff`,
          priceComponent: Object.keys(zone.zone).map((key) => {
            const labelKey = key as keyof typeof content.labels
            const chargeKey = key as keyof typeof zone.zone

            const priceSpecification: UnitPriceSpecification = {
              '@type': 'UnitPriceSpecification',
              description: getCommonDesc(key),
              name: content.labels[labelKey],
              price: formatPrice(zone.zone[chargeKey]),
              priceCurrency: 'GBP',
              unitCode: getCommonCode(key),
            }

            // Remove unnecessary fields if charge is free
            if (zone.zone[chargeKey] === -1) {
              delete priceSpecification.description
              delete priceSpecification.priceCurrency
              delete priceSpecification.unitCode
            }

            return priceSpecification
          }),
        },
      },
    }
  })

  const zoneListWithPlan = RoamingZones.filter((zone) => ['EU', 'EEA'].includes(zone.name)).map(
    (zone): WithContext<Service> => {
      return {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: `Roaming in ${zone.name} with a giffgaff plan`,
        offers: {
          '@type': 'Offer',
          name: `Roaming in ${zone.name} with a giffgaff plan`,
          areaServed: roamingCharges.reduce<WithContext<Country>[]>((carry, charge) => {
            if (charge.zone === zone.name) {
              carry.push({
                '@context': 'https://schema.org',
                '@type': 'Country',
                name: charge.name,
              })
            }

            return carry
          }, []),
          availability: 'https://schema.org/InStock',
          priceSpecification: {
            '@type': 'CompoundPriceSpecification',
            name: `Roaming in ${zone.name} with a giffgaff plan`,
            priceComponent: Object.keys(zone.zone).map((key) => {
              const labelKey = key as keyof typeof content.labels

              const priceSpecification: UnitPriceSpecification = {
                '@type': 'UnitPriceSpecification',
                name: content.labels[labelKey],
                price: 'Free',
              }

              return priceSpecification
            }),
          },
        },
      }
    }
  )

  return [...paygZoneList, ...zoneListWithPlan]
}
